/**
 * Bitcoin Derivation Path Module
 *
 * This module provides type-safe standard derivation paths for Bitcoin wallets.
 * It supports all major BIP standards (BIP44, BIP49, BIP84, BIP86) and provides
 * both string and object representations of derivation paths.
 *
 * Derivation paths follow the format: m / purpose' / coin_type' / account' / change / address_index
 * - purpose: BIP number (44, 49, 84, 86)
 * - coin_type: 0 for mainnet, 1 for testnet
 * - account: Account index (hardened)
 * - change: 0 for receive addresses, 1 for change addresses
 * - address_index: Sequential address index
 */

import type { NetworkTypeKey, NetworkTypeValue } from './Network';
import { NetworkReverseMap } from './Network';
import type { DerivationPurposeNumber } from './Purpose';

// ============================================================================
// 1. CONSTANTS: Coin Type Configuration
// ============================================================================

/**
 * Network type constants for filtering and path operations.
 * These constants reference the network keys from Network.ts.
 */
const NETWORK_MAINNET = NetworkReverseMap.mainnet;
const NETWORK_TESTNET = NetworkReverseMap.testnet;
const NETWORK_REGTEST = NetworkReverseMap.regtest;

/**
 * Bitcoin mainnet coin type according to SLIP-0044.
 * Used in derivation paths as the coin_type level: m/purpose'/0'/...
 */
export const COIN_TYPE_MAINNET = 0 as const;

/**
 * Bitcoin testnet coin type according to SLIP-0044.
 * Used in derivation paths as the coin_type level: m/purpose'/1'/...
 */
export const COIN_TYPE_TESTNET = 1 as const;

/**
 * Union type of all supported coin types.
 * Equivalent to: 0 | 1
 */
export type CoinType = typeof COIN_TYPE_MAINNET | typeof COIN_TYPE_TESTNET;

// ============================================================================
// 2. ENUMS: Account, Change, and Address Indices
// ============================================================================

/**
 * Standard account indices following BIP44 account discovery.
 * Accounts are used to segregate different purposes or identities.
 * Each account operates independently with its own receiving and change addresses.
 *
 * The gap limit for account discovery is typically 20, meaning if 20 consecutive
 * accounts have no transaction history, the search stops.
 */
export enum AccountIndex {
  ACCOUNT_0 = 0,
  ACCOUNT_1 = 1,
  ACCOUNT_2 = 2,
  ACCOUNT_3 = 3,
  ACCOUNT_4 = 4,
  ACCOUNT_5 = 5,
  ACCOUNT_6 = 6,
  ACCOUNT_7 = 7,
  ACCOUNT_8 = 8,
  ACCOUNT_9 = 9,
  ACCOUNT_10 = 10,
  ACCOUNT_11 = 11,
  ACCOUNT_12 = 12,
  ACCOUNT_13 = 13,
  ACCOUNT_14 = 14,
  ACCOUNT_15 = 15,
  ACCOUNT_16 = 16,
  ACCOUNT_17 = 17,
  ACCOUNT_18 = 18,
  ACCOUNT_19 = 19,
}

/**
 * Change index determines the type of address chain.
 *
 * - RECEIVE (0): External chain - addresses given to others to receive payments
 * - CHANGE (1): Internal chain - addresses used for receiving change from transactions
 *
 * This separation improves privacy by not reusing addresses and keeping change
 * separate from receiving addresses.
 */
export enum ChangeIndex {
  RECEIVE = 0,
  CHANGE = 1,
}

/**
 * Standard address indices within a change chain.
 * Addresses are generated sequentially starting from index 0.
 *
 * The gap limit for address discovery is typically 20, meaning if 20 consecutive
 * addresses have no transaction history, the search stops for that chain.
 */
export enum AddressIndex {
  ADDRESS_0 = 0,
  ADDRESS_1 = 1,
  ADDRESS_2 = 2,
  ADDRESS_3 = 3,
  ADDRESS_4 = 4,
  ADDRESS_5 = 5,
  ADDRESS_6 = 6,
  ADDRESS_7 = 7,
  ADDRESS_8 = 8,
  ADDRESS_9 = 9,
  ADDRESS_10 = 10,
  ADDRESS_11 = 11,
  ADDRESS_12 = 12,
  ADDRESS_13 = 13,
  ADDRESS_14 = 14,
  ADDRESS_15 = 15,
  ADDRESS_16 = 16,
  ADDRESS_17 = 17,
  ADDRESS_18 = 18,
  ADDRESS_19 = 19,
}

// ============================================================================
// 3. TYPES: Derivation Path Representations
// ============================================================================

/**
 * Object representation of a BIP32 derivation path.
 * Each field corresponds to a level in the derivation hierarchy.
 *
 * @property purpose - BIP number (44, 49, 84, 86, etc.)
 * @property coinType - Coin type (0 for mainnet, 1 for testnet)
 * @property account - Account index (0-2^31-1, hardened)
 * @property change - Change index (0 for receive, 1 for change)
 * @property addressIndex - Address index (0-2^31-1, non-hardened)
 */
export interface DerivationPathObject {
  purpose: DerivationPurposeNumber;
  coinType: CoinType;
  account: number;
  change: ChangeIndex;
  addressIndex: number;
}

/**
 * String representation of a derivation path.
 * Format: m/purpose'/coinType'/account'/change/addressIndex
 *
 * Examples:
 * - "m/44'/0'/0'/0/0" - First receiving address, BIP44 (Legacy)
 * - "m/84'/0'/0'/0/0" - First receiving address, BIP84 (Native SegWit)
 * - "m/84'/0'/0'/1/0" - First change address, BIP84
 * - "m/86'/0'/1'/0/0" - First receiving address of second account, BIP86 (Taproot)
 */
export type DerivationPathString = string;

/**
 * Complete derivation path with both string and object representations.
 * This allows working with paths in the most convenient format.
 */
export interface StandardDerivationPath {
  /** String representation: "m/44'/0'/0'/0/0" */
  path: DerivationPathString;
  /** Object representation with individual components */
  components: DerivationPathObject;
}

// ============================================================================
// 4. UTILITY FUNCTIONS: Core Path Operations
// ============================================================================

/**
 * Builds a complete derivation path from individual components.
 *
 * @param purpose - BIP number (44, 49, 84, 86)
 * @param coinType - Coin type (0 for mainnet, 1 for testnet)
 * @param account - Account index (0 or higher)
 * @param change - Change index (0 for receive, 1 for change)
 * @param addressIndex - Address index (0 or higher)
 * @returns Object containing both string and object representations
 *
 * @example
 * ```typescript
 * // Build a BIP84 Native SegWit path
 * const path = buildDerivationPath(84, 0, 0, ChangeIndex.RECEIVE, 0);
 * console.log(path.path); // "m/84'/0'/0'/0/0"
 * console.log(path.components.purpose); // 84
 * ```
 */
export function buildDerivationPath(
  purpose: DerivationPurposeNumber,
  coinType: CoinType,
  account: number,
  change: ChangeIndex,
  addressIndex: number
): StandardDerivationPath {
  const path = `m/${purpose}'/${coinType}'/${account}'/${change}/${addressIndex}`;

  return {
    path,
    components: {
      purpose,
      coinType,
      account,
      change,
      addressIndex,
    },
  };
}

/**
 * Parses a derivation path string into its component parts.
 *
 * @param path - Derivation path string (e.g., "m/84'/0'/0'/0/0")
 * @returns Object containing the parsed components
 * @throws Error if the path format is invalid
 *
 * @example
 * ```typescript
 * const components = parseDerivationPath("m/84'/0'/0'/0/0");
 * console.log(components.purpose); // 84
 * console.log(components.coinType); // 0
 * console.log(components.account); // 0
 * console.log(components.change); // 0
 * console.log(components.addressIndex); // 0
 * ```
 */
export function parseDerivationPath(path: DerivationPathString): DerivationPathObject {
  // Remove 'm/' prefix if present
  const cleanPath = path.replace(/^m\//, '');

  // Split by '/' to get individual levels
  const parts = cleanPath.split('/');

  if (parts.length !== 5) {
    throw new Error(
      `Invalid derivation path format: ${path}. Expected format: m/purpose'/coinType'/account'/change/addressIndex`
    );
  }

  // Ensure all parts exist
  const [part0, part1, part2, part3, part4] = parts;
  if (!part0 || !part1 || !part2 || !part3 || !part4) {
    throw new Error(`Invalid derivation path: ${path}. Missing path components.`);
  }

  // Parse each level, removing hardened markers (')
  const purpose = parseInt(part0.replace("'", ''), 10) as DerivationPurposeNumber;
  const coinType = parseInt(part1.replace("'", ''), 10) as CoinType;
  const account = parseInt(part2.replace("'", ''), 10);
  const change = parseInt(part3, 10) as ChangeIndex;
  const addressIndex = parseInt(part4, 10);

  // Validate parsed values
  if (isNaN(purpose) || isNaN(coinType) || isNaN(account) || isNaN(change) || isNaN(addressIndex)) {
    throw new Error(`Invalid derivation path: ${path}. All components must be valid numbers.`);
  }

  return {
    purpose,
    coinType,
    account,
    change,
    addressIndex,
  };
}

/**
 * Gets the appropriate coin type for a given network.
 *
 * @param network - Network type (key or value)
 * @returns Coin type (0 for mainnet, 1 for testnet/regtest)
 *
 * @example
 * ```typescript
 * const mainnetCoin = getCoinTypeForNetwork('MAINNET'); // 0
 * const testnetCoin = getCoinTypeForNetwork('testnet'); // 1
 * const regtestCoin = getCoinTypeForNetwork('REGTEST'); // 1
 * ```
 */
export function getCoinTypeForNetwork(network: NetworkTypeKey | NetworkTypeValue): CoinType {
  // Convert to uppercase key for consistent comparison
  const networkKey =
    typeof network === 'string' && network.toUpperCase() in NetworkReverseMap
      ? (network.toUpperCase() as NetworkTypeKey)
      : (NetworkReverseMap[network as NetworkTypeValue] as NetworkTypeKey);

  return networkKey === NETWORK_MAINNET ? COIN_TYPE_MAINNET : COIN_TYPE_TESTNET;
}

/**
 * Gets a standard derivation path for a specific BIP standard and network.
 * Provides default values for optional parameters.
 *
 * @param purpose - BIP number (44, 49, 84, 86)
 * @param network - Network type (MAINNET, TESTNET, REGTEST)
 * @param account - Account index (default: 0)
 * @param change - Change index (default: 0 for receive)
 * @param addressIndex - Address index (default: 0)
 * @returns Complete derivation path with both string and object representations
 *
 * @example
 * ```typescript
 * // Get first receiving address for BIP84 on mainnet
 * const path1 = getStandardPath(84, 'MAINNET');
 * console.log(path1.path); // "m/84'/0'/0'/0/0"
 *
 * // Get first change address for BIP84 on testnet
 * const path2 = getStandardPath(84, 'TESTNET', 0, ChangeIndex.CHANGE, 0);
 * console.log(path2.path); // "m/84'/1'/0'/1/0"
 *
 * // Get second account's first address
 * const path3 = getStandardPath(86, 'MAINNET', 1);
 * console.log(path3.path); // "m/86'/0'/1'/0/0"
 * ```
 */
export function getStandardPath(
  purpose: DerivationPurposeNumber,
  network: NetworkTypeKey | NetworkTypeValue,
  account: number = 0,
  change: ChangeIndex = ChangeIndex.RECEIVE,
  addressIndex: number = 0
): StandardDerivationPath {
  const coinType = getCoinTypeForNetwork(network);
  return buildDerivationPath(purpose, coinType, account, change, addressIndex);
}

// ============================================================================
// 5. STANDARD PATH MAPS: Pre-defined Common Paths
// ============================================================================

/**
 * Standard derivation paths for BIP44 (Legacy P2PKH addresses).
 * Format: m/44'/coinType'/account'/change/addressIndex
 */
export const STANDARD_PATHS_BIP44 = {
  /** First receiving address (m/44'/0'/0'/0/0) - Most common */
  FIRST_RECEIVE_MAINNET: buildDerivationPath(
    44,
    COIN_TYPE_MAINNET,
    AccountIndex.ACCOUNT_0,
    ChangeIndex.RECEIVE,
    AddressIndex.ADDRESS_0
  ),

  /** First change address (m/44'/0'/0'/1/0) */
  FIRST_CHANGE_MAINNET: buildDerivationPath(
    44,
    COIN_TYPE_MAINNET,
    AccountIndex.ACCOUNT_0,
    ChangeIndex.CHANGE,
    AddressIndex.ADDRESS_0
  ),

  /** Second account first receive (m/44'/0'/1'/0/0) */
  SECOND_ACCOUNT_MAINNET: buildDerivationPath(
    44,
    COIN_TYPE_MAINNET,
    AccountIndex.ACCOUNT_1,
    ChangeIndex.RECEIVE,
    AddressIndex.ADDRESS_0
  ),

  /** First receiving address testnet (m/44'/1'/0'/0/0) */
  FIRST_RECEIVE_TESTNET: buildDerivationPath(
    44,
    COIN_TYPE_TESTNET,
    AccountIndex.ACCOUNT_0,
    ChangeIndex.RECEIVE,
    AddressIndex.ADDRESS_0
  ),

  /** First change address testnet (m/44'/1'/0'/1/0) */
  FIRST_CHANGE_TESTNET: buildDerivationPath(
    44,
    COIN_TYPE_TESTNET,
    AccountIndex.ACCOUNT_0,
    ChangeIndex.CHANGE,
    AddressIndex.ADDRESS_0
  ),
} as const;

/**
 * Standard derivation paths for BIP49 (Nested SegWit P2SH-P2WPKH addresses).
 * Format: m/49'/coinType'/account'/change/addressIndex
 */
export const STANDARD_PATHS_BIP49 = {
  /** First receiving address (m/49'/0'/0'/0/0) - Most common */
  FIRST_RECEIVE_MAINNET: buildDerivationPath(
    49,
    COIN_TYPE_MAINNET,
    AccountIndex.ACCOUNT_0,
    ChangeIndex.RECEIVE,
    AddressIndex.ADDRESS_0
  ),

  /** First change address (m/49'/0'/0'/1/0) */
  FIRST_CHANGE_MAINNET: buildDerivationPath(
    49,
    COIN_TYPE_MAINNET,
    AccountIndex.ACCOUNT_0,
    ChangeIndex.CHANGE,
    AddressIndex.ADDRESS_0
  ),

  /** Second account first receive (m/49'/0'/1'/0/0) */
  SECOND_ACCOUNT_MAINNET: buildDerivationPath(
    49,
    COIN_TYPE_MAINNET,
    AccountIndex.ACCOUNT_1,
    ChangeIndex.RECEIVE,
    AddressIndex.ADDRESS_0
  ),

  /** First receiving address testnet (m/49'/1'/0'/0/0) */
  FIRST_RECEIVE_TESTNET: buildDerivationPath(
    49,
    COIN_TYPE_TESTNET,
    AccountIndex.ACCOUNT_0,
    ChangeIndex.RECEIVE,
    AddressIndex.ADDRESS_0
  ),

  /** First change address testnet (m/49'/1'/0'/1/0) */
  FIRST_CHANGE_TESTNET: buildDerivationPath(
    49,
    COIN_TYPE_TESTNET,
    AccountIndex.ACCOUNT_0,
    ChangeIndex.CHANGE,
    AddressIndex.ADDRESS_0
  ),
} as const;

/**
 * Standard derivation paths for BIP84 (Native SegWit P2WPKH addresses).
 * Format: m/84'/coinType'/account'/change/addressIndex
 */
export const STANDARD_PATHS_BIP84 = {
  /** First receiving address (m/84'/0'/0'/0/0) - Most common */
  FIRST_RECEIVE_MAINNET: buildDerivationPath(
    84,
    COIN_TYPE_MAINNET,
    AccountIndex.ACCOUNT_0,
    ChangeIndex.RECEIVE,
    AddressIndex.ADDRESS_0
  ),

  /** First change address (m/84'/0'/0'/1/0) */
  FIRST_CHANGE_MAINNET: buildDerivationPath(
    84,
    COIN_TYPE_MAINNET,
    AccountIndex.ACCOUNT_0,
    ChangeIndex.CHANGE,
    AddressIndex.ADDRESS_0
  ),

  /** Second account first receive (m/84'/0'/1'/0/0) */
  SECOND_ACCOUNT_MAINNET: buildDerivationPath(
    84,
    COIN_TYPE_MAINNET,
    AccountIndex.ACCOUNT_1,
    ChangeIndex.RECEIVE,
    AddressIndex.ADDRESS_0
  ),

  /** First receiving address testnet (m/84'/1'/0'/0/0) */
  FIRST_RECEIVE_TESTNET: buildDerivationPath(
    84,
    COIN_TYPE_TESTNET,
    AccountIndex.ACCOUNT_0,
    ChangeIndex.RECEIVE,
    AddressIndex.ADDRESS_0
  ),

  /** First change address testnet (m/84'/1'/0'/1/0) */
  FIRST_CHANGE_TESTNET: buildDerivationPath(
    84,
    COIN_TYPE_TESTNET,
    AccountIndex.ACCOUNT_0,
    ChangeIndex.CHANGE,
    AddressIndex.ADDRESS_0
  ),
} as const;

/**
 * Standard derivation paths for BIP86 (Taproot P2TR addresses).
 * Format: m/86'/coinType'/account'/change/addressIndex
 */
export const STANDARD_PATHS_BIP86 = {
  /** First receiving address (m/86'/0'/0'/0/0) - Most common */
  FIRST_RECEIVE_MAINNET: buildDerivationPath(
    86,
    COIN_TYPE_MAINNET,
    AccountIndex.ACCOUNT_0,
    ChangeIndex.RECEIVE,
    AddressIndex.ADDRESS_0
  ),

  /** First change address (m/86'/0'/0'/1/0) */
  FIRST_CHANGE_MAINNET: buildDerivationPath(
    86,
    COIN_TYPE_MAINNET,
    AccountIndex.ACCOUNT_0,
    ChangeIndex.CHANGE,
    AddressIndex.ADDRESS_0
  ),

  /** Second account first receive (m/86'/0'/1'/0/0) */
  SECOND_ACCOUNT_MAINNET: buildDerivationPath(
    86,
    COIN_TYPE_MAINNET,
    AccountIndex.ACCOUNT_1,
    ChangeIndex.RECEIVE,
    AddressIndex.ADDRESS_0
  ),

  /** First receiving address testnet (m/86'/1'/0'/0/0) */
  FIRST_RECEIVE_TESTNET: buildDerivationPath(
    86,
    COIN_TYPE_TESTNET,
    AccountIndex.ACCOUNT_0,
    ChangeIndex.RECEIVE,
    AddressIndex.ADDRESS_0
  ),

  /** First change address testnet (m/86'/1'/0'/1/0) */
  FIRST_CHANGE_TESTNET: buildDerivationPath(
    86,
    COIN_TYPE_TESTNET,
    AccountIndex.ACCOUNT_0,
    ChangeIndex.CHANGE,
    AddressIndex.ADDRESS_0
  ),
} as const;

// ============================================================================
// 6. TYPE EXPORTS: Standard Path Map Types
// ============================================================================

/**
 * Type of the BIP44 standard paths map.
 */
export type StandardPathsBip44 = typeof STANDARD_PATHS_BIP44;

/**
 * Type of the BIP49 standard paths map.
 */
export type StandardPathsBip49 = typeof STANDARD_PATHS_BIP49;

/**
 * Type of the BIP84 standard paths map.
 */
export type StandardPathsBip84 = typeof STANDARD_PATHS_BIP84;

/**
 * Type of the BIP86 standard paths map.
 */
export type StandardPathsBip86 = typeof STANDARD_PATHS_BIP86;

// ============================================================================
// 7. LEGACY PATHS: Historical and Less Common Formats
// ============================================================================

/**
 * Legacy and historical derivation paths.
 * These paths are less commonly used today but may be encountered in older wallets.
 *
 * Includes:
 * - Electrum Legacy format (m/0'/change/index)
 * - BIP32 basic format (m/account'/change/index)
 */
export const LEGACY_PATHS = {
  /** Electrum Legacy first receive address (m/0'/0/0) */
  ELECTRUM_LEGACY_FIRST_RECEIVE: {
    path: "m/0'/0/0" as DerivationPathString,
    components: {
      purpose: 0 as DerivationPurposeNumber,
      coinType: COIN_TYPE_MAINNET,
      account: 0,
      change: ChangeIndex.RECEIVE,
      addressIndex: 0,
    } as DerivationPathObject,
  } as StandardDerivationPath,

  /** Electrum Legacy first change address (m/0'/1/0) */
  ELECTRUM_LEGACY_FIRST_CHANGE: {
    path: "m/0'/1/0" as DerivationPathString,
    components: {
      purpose: 0 as DerivationPurposeNumber,
      coinType: COIN_TYPE_MAINNET,
      account: 0,
      change: ChangeIndex.CHANGE,
      addressIndex: 0,
    } as DerivationPathObject,
  } as StandardDerivationPath,

  /** BIP32 basic first receive (m/0'/0/0) - same path as Electrum but different interpretation */
  BIP32_BASIC_FIRST_RECEIVE: {
    path: "m/0'/0/0" as DerivationPathString,
    components: {
      purpose: 32 as DerivationPurposeNumber,
      coinType: COIN_TYPE_MAINNET,
      account: 0,
      change: ChangeIndex.RECEIVE,
      addressIndex: 0,
    } as DerivationPathObject,
  } as StandardDerivationPath,

  /** BIP32 basic first change (m/0'/1/0) */
  BIP32_BASIC_FIRST_CHANGE: {
    path: "m/0'/1/0" as DerivationPathString,
    components: {
      purpose: 32 as DerivationPurposeNumber,
      coinType: COIN_TYPE_MAINNET,
      account: 0,
      change: ChangeIndex.CHANGE,
      addressIndex: 0,
    } as DerivationPathObject,
  } as StandardDerivationPath,

  /** BIP32 basic non-hardened variant (m/0/0/0) */
  BIP32_NON_HARDENED: {
    path: 'm/0/0/0' as DerivationPathString,
    components: {
      purpose: 32 as DerivationPurposeNumber,
      coinType: COIN_TYPE_MAINNET,
      account: 0,
      change: ChangeIndex.RECEIVE,
      addressIndex: 0,
    } as DerivationPathObject,
  } as StandardDerivationPath,
} as const;

/**
 * Multi-signature derivation paths for BIP45 and BIP48.
 * These paths are used for multi-signature wallet setups.
 *
 * - BIP45: m/45'/cosigner/change/index (no coin_type or account hardening)
 * - BIP48: m/48'/coin_type'/account'/script_type'/change/index
 *   - script_type 1 = P2WSH-P2SH (wrapped witness script hash)
 *   - script_type 2 = P2WSH (native witness script hash)
 */
export const MULTISIG_PATHS = {
  /** BIP45 cosigner 0 first receive (m/45'/0/0/0) */
  BIP45_COSIGNER_0_RECEIVE_MAINNET: {
    path: "m/45'/0/0/0" as DerivationPathString,
    components: {
      purpose: 45 as DerivationPurposeNumber,
      coinType: COIN_TYPE_MAINNET,
      account: 0,
      change: ChangeIndex.RECEIVE,
      addressIndex: 0,
    } as DerivationPathObject,
  } as StandardDerivationPath,

  /** BIP45 cosigner 0 first change (m/45'/0/1/0) */
  BIP45_COSIGNER_0_CHANGE_MAINNET: {
    path: "m/45'/0/1/0" as DerivationPathString,
    components: {
      purpose: 45 as DerivationPurposeNumber,
      coinType: COIN_TYPE_MAINNET,
      account: 0,
      change: ChangeIndex.CHANGE,
      addressIndex: 0,
    } as DerivationPathObject,
  } as StandardDerivationPath,

  /** BIP45 cosigner 0 testnet (m/45'/0/0/0) */
  BIP45_COSIGNER_0_RECEIVE_TESTNET: {
    path: "m/45'/0/0/0" as DerivationPathString,
    components: {
      purpose: 45 as DerivationPurposeNumber,
      coinType: COIN_TYPE_TESTNET,
      account: 0,
      change: ChangeIndex.RECEIVE,
      addressIndex: 0,
    } as DerivationPathObject,
  } as StandardDerivationPath,

  /** BIP48 P2WSH-P2SH first receive mainnet (m/48'/0'/0'/1'/0/0) */
  BIP48_P2WSH_P2SH_RECEIVE_MAINNET: {
    path: "m/48'/0'/0'/1'/0/0" as DerivationPathString,
    components: {
      purpose: 48 as DerivationPurposeNumber,
      coinType: COIN_TYPE_MAINNET,
      account: 0,
      change: ChangeIndex.RECEIVE,
      addressIndex: 0,
    } as DerivationPathObject,
  } as StandardDerivationPath,

  /** BIP48 P2WSH-P2SH first change mainnet (m/48'/0'/0'/1'/1/0) */
  BIP48_P2WSH_P2SH_CHANGE_MAINNET: {
    path: "m/48'/0'/0'/1'/1/0" as DerivationPathString,
    components: {
      purpose: 48 as DerivationPurposeNumber,
      coinType: COIN_TYPE_MAINNET,
      account: 0,
      change: ChangeIndex.CHANGE,
      addressIndex: 0,
    } as DerivationPathObject,
  } as StandardDerivationPath,

  /** BIP48 P2WSH first receive mainnet (m/48'/0'/0'/2'/0/0) */
  BIP48_P2WSH_RECEIVE_MAINNET: {
    path: "m/48'/0'/0'/2'/0/0" as DerivationPathString,
    components: {
      purpose: 48 as DerivationPurposeNumber,
      coinType: COIN_TYPE_MAINNET,
      account: 0,
      change: ChangeIndex.RECEIVE,
      addressIndex: 0,
    } as DerivationPathObject,
  } as StandardDerivationPath,

  /** BIP48 P2WSH first change mainnet (m/48'/0'/0'/2'/1/0) */
  BIP48_P2WSH_CHANGE_MAINNET: {
    path: "m/48'/0'/0'/2'/1/0" as DerivationPathString,
    components: {
      purpose: 48 as DerivationPurposeNumber,
      coinType: COIN_TYPE_MAINNET,
      account: 0,
      change: ChangeIndex.CHANGE,
      addressIndex: 0,
    } as DerivationPathObject,
  } as StandardDerivationPath,

  /** BIP48 P2WSH-P2SH testnet (m/48'/1'/0'/1'/0/0) */
  BIP48_P2WSH_P2SH_RECEIVE_TESTNET: {
    path: "m/48'/1'/0'/1'/0/0" as DerivationPathString,
    components: {
      purpose: 48 as DerivationPurposeNumber,
      coinType: COIN_TYPE_TESTNET,
      account: 0,
      change: ChangeIndex.RECEIVE,
      addressIndex: 0,
    } as DerivationPathObject,
  } as StandardDerivationPath,

  /** BIP48 P2WSH testnet (m/48'/1'/0'/2'/0/0) */
  BIP48_P2WSH_RECEIVE_TESTNET: {
    path: "m/48'/1'/0'/2'/0/0" as DerivationPathString,
    components: {
      purpose: 48 as DerivationPurposeNumber,
      coinType: COIN_TYPE_TESTNET,
      account: 0,
      change: ChangeIndex.RECEIVE,
      addressIndex: 0,
    } as DerivationPathObject,
  } as StandardDerivationPath,
} as const;

/**
 * Combined collection of all historical and less common derivation paths.
 * Includes both legacy formats and multi-signature paths.
 */
export const HISTORICAL_PATHS = {
  ...LEGACY_PATHS,
  ...MULTISIG_PATHS,
} as const;

/**
 * Type of the legacy paths map.
 */
export type LegacyPathsType = typeof LEGACY_PATHS;

/**
 * Type of the multisig paths map.
 */
export type MultisigPathsType = typeof MULTISIG_PATHS;

/**
 * Type of the historical paths map.
 */
export type HistoricalPathsType = typeof HISTORICAL_PATHS;

// ============================================================================
// 8. NETWORK-BASED PATH COLLECTIONS
// ============================================================================

/**
 * All derivation paths for Bitcoin mainnet.
 * Includes common standard paths (BIP44, 49, 84, 86), legacy paths, and multisig paths.
 */
export const PATHS_BY_NETWORK_MAINNET = {
  // Common standard paths
  ...Object.fromEntries(
    Object.entries(STANDARD_PATHS_BIP44)
      .filter(([key]) => key.includes(NETWORK_MAINNET))
      .map(([key, value]) => [`BIP44_${key}`, value])
  ),
  ...Object.fromEntries(
    Object.entries(STANDARD_PATHS_BIP49)
      .filter(([key]) => key.includes(NETWORK_MAINNET))
      .map(([key, value]) => [`BIP49_${key}`, value])
  ),
  ...Object.fromEntries(
    Object.entries(STANDARD_PATHS_BIP84)
      .filter(([key]) => key.includes(NETWORK_MAINNET))
      .map(([key, value]) => [`BIP84_${key}`, value])
  ),
  ...Object.fromEntries(
    Object.entries(STANDARD_PATHS_BIP86)
      .filter(([key]) => key.includes(NETWORK_MAINNET))
      .map(([key, value]) => [`BIP86_${key}`, value])
  ),
  // Legacy paths (network-agnostic but included in mainnet)
  ...LEGACY_PATHS,
  // Multisig paths for mainnet
  ...Object.fromEntries(
    Object.entries(MULTISIG_PATHS).filter(([key]) => key.includes(NETWORK_MAINNET))
  ),
} as const;

/**
 * All derivation paths for Bitcoin testnet.
 * Includes common standard paths (BIP44, 49, 84, 86), legacy paths, and multisig paths.
 */
export const PATHS_BY_NETWORK_TESTNET = {
  // Common standard paths
  ...Object.fromEntries(
    Object.entries(STANDARD_PATHS_BIP44)
      .filter(([key]) => key.includes(NETWORK_TESTNET))
      .map(([key, value]) => [`BIP44_${key}`, value])
  ),
  ...Object.fromEntries(
    Object.entries(STANDARD_PATHS_BIP49)
      .filter(([key]) => key.includes(NETWORK_TESTNET))
      .map(([key, value]) => [`BIP49_${key}`, value])
  ),
  ...Object.fromEntries(
    Object.entries(STANDARD_PATHS_BIP84)
      .filter(([key]) => key.includes(NETWORK_TESTNET))
      .map(([key, value]) => [`BIP84_${key}`, value])
  ),
  ...Object.fromEntries(
    Object.entries(STANDARD_PATHS_BIP86)
      .filter(([key]) => key.includes(NETWORK_TESTNET))
      .map(([key, value]) => [`BIP86_${key}`, value])
  ),
  // Legacy paths (network-agnostic but included in testnet)
  ...LEGACY_PATHS,
  // Multisig paths for testnet
  ...Object.fromEntries(
    Object.entries(MULTISIG_PATHS).filter(([key]) => key.includes(NETWORK_TESTNET))
  ),
} as const;

/**
 * All derivation paths for Bitcoin regtest.
 * Uses the same coin type as testnet (1).
 * Includes common standard paths (BIP44, 49, 84, 86), legacy paths, and multisig paths.
 */
export const PATHS_BY_NETWORK_REGTEST = {
  // Common standard paths (using testnet coin type)
  ...Object.fromEntries(
    Object.entries(STANDARD_PATHS_BIP44)
      .filter(([key]) => key.includes(NETWORK_TESTNET))
      .map(([key, value]) => [`BIP44_${key.replace(NETWORK_TESTNET, NETWORK_REGTEST)}`, value])
  ),
  ...Object.fromEntries(
    Object.entries(STANDARD_PATHS_BIP49)
      .filter(([key]) => key.includes(NETWORK_TESTNET))
      .map(([key, value]) => [`BIP49_${key.replace(NETWORK_TESTNET, NETWORK_REGTEST)}`, value])
  ),
  ...Object.fromEntries(
    Object.entries(STANDARD_PATHS_BIP84)
      .filter(([key]) => key.includes(NETWORK_TESTNET))
      .map(([key, value]) => [`BIP84_${key.replace(NETWORK_TESTNET, NETWORK_REGTEST)}`, value])
  ),
  ...Object.fromEntries(
    Object.entries(STANDARD_PATHS_BIP86)
      .filter(([key]) => key.includes(NETWORK_TESTNET))
      .map(([key, value]) => [`BIP86_${key.replace(NETWORK_TESTNET, NETWORK_REGTEST)}`, value])
  ),
  // Legacy paths (network-agnostic but included in regtest)
  ...LEGACY_PATHS,
  // Multisig paths for regtest (using testnet paths)
  ...Object.fromEntries(
    Object.entries(MULTISIG_PATHS)
      .filter(([key]) => key.includes(NETWORK_TESTNET))
      .map(([key, value]) => [`${key.replace(NETWORK_TESTNET, NETWORK_REGTEST)}`, value])
  ),
} as const;

/**
 * Type of the mainnet paths collection.
 */
export type PathsByNetworkMainnet = typeof PATHS_BY_NETWORK_MAINNET;

/**
 * Type of the testnet paths collection.
 */
export type PathsByNetworkTestnet = typeof PATHS_BY_NETWORK_TESTNET;

/**
 * Type of the regtest paths collection.
 */
export type PathsByNetworkRegtest = typeof PATHS_BY_NETWORK_REGTEST;

// ============================================================================
// 9. NETWORK FILTERING UTILITY FUNCTIONS
// ============================================================================

/**
 * Gets all derivation paths for a specific network.
 * Includes common, legacy, and multisig paths.
 *
 * @param network - Network type (MAINNET, TESTNET, or REGTEST)
 * @returns Array of all derivation paths for the specified network
 *
 * @example
 * ```typescript
 * const mainnetPaths = getPathsForNetwork('MAINNET');
 * console.log(mainnetPaths.length); // All mainnet paths
 *
 * const testnetPaths = getPathsForNetwork('testnet');
 * console.log(testnetPaths[0].path); // First testnet path
 * ```
 */
export function getPathsForNetwork(
  network: NetworkTypeKey | NetworkTypeValue
): StandardDerivationPath[] {
  const networkKey =
    typeof network === 'string' && network.toUpperCase() in NetworkReverseMap
      ? (network.toUpperCase() as NetworkTypeKey)
      : (NetworkReverseMap[network as NetworkTypeValue] as NetworkTypeKey);

  switch (networkKey) {
    case NETWORK_MAINNET:
      return Object.values(PATHS_BY_NETWORK_MAINNET);
    case NETWORK_TESTNET:
      return Object.values(PATHS_BY_NETWORK_TESTNET);
    case NETWORK_REGTEST:
      return Object.values(PATHS_BY_NETWORK_REGTEST);
    default:
      throw new Error(`Unknown network: ${network}`);
  }
}

/**
 * Gets only the common/standard derivation paths for a specific network.
 * Returns paths for BIP44, BIP49, BIP84, and BIP86.
 *
 * @param network - Network type (MAINNET, TESTNET, or REGTEST)
 * @returns Array of common derivation paths
 *
 * @example
 * ```typescript
 * const commonPaths = getCommonPathsForNetwork('MAINNET');
 * // Returns only BIP44, 49, 84, 86 mainnet paths
 * ```
 */
export function getCommonPathsForNetwork(
  network: NetworkTypeKey | NetworkTypeValue
): StandardDerivationPath[] {
  const coinType = getCoinTypeForNetwork(network);

  const allCommonPaths = [
    ...Object.values(STANDARD_PATHS_BIP44),
    ...Object.values(STANDARD_PATHS_BIP49),
    ...Object.values(STANDARD_PATHS_BIP84),
    ...Object.values(STANDARD_PATHS_BIP86),
  ];

  return allCommonPaths.filter((path) => path.components.coinType === coinType);
}

/**
 * Gets only legacy/historical derivation paths for a specific network.
 * Returns Electrum Legacy and BIP32 basic paths.
 *
 * Note: Legacy paths are network-agnostic and don't use the coin_type field,
 * so the same paths are returned for all networks.
 *
 * @param _network - Network type (MAINNET, TESTNET, or REGTEST) - unused but kept for API consistency
 * @returns Array of legacy derivation paths
 *
 * @example
 * ```typescript
 * const legacyPaths = getLegacyPathsForNetwork('MAINNET');
 * // Returns Electrum Legacy and BIP32 paths
 * ```
 */
export function getLegacyPathsForNetwork(
  _network: NetworkTypeKey | NetworkTypeValue
): StandardDerivationPath[] {
  // Legacy paths are network-agnostic, return all
  return Object.values(LEGACY_PATHS);
}

/**
 * Gets only multi-signature derivation paths for a specific network.
 * Returns BIP45 and BIP48 paths.
 *
 * @param network - Network type (MAINNET, TESTNET, or REGTEST)
 * @returns Array of multisig derivation paths
 *
 * @example
 * ```typescript
 * const multisigPaths = getMultisigPathsForNetwork('MAINNET');
 * // Returns BIP45 and BIP48 mainnet paths
 * ```
 */
export function getMultisigPathsForNetwork(
  network: NetworkTypeKey | NetworkTypeValue
): StandardDerivationPath[] {
  const coinType = getCoinTypeForNetwork(network);

  return Object.values(MULTISIG_PATHS).filter((path) => path.components.coinType === coinType);
}

/**
 * Gets all derivation paths grouped by network.
 *
 * @returns Object containing arrays of paths for each network
 *
 * @example
 * ```typescript
 * const allPaths = getAllPathsByNetwork();
 * console.log(allPaths.mainnet.length); // Number of mainnet paths
 * console.log(allPaths.testnet.length); // Number of testnet paths
 * console.log(allPaths.regtest.length); // Number of regtest paths
 * ```
 */
export function getAllPathsByNetwork(): {
  mainnet: StandardDerivationPath[];
  testnet: StandardDerivationPath[];
  regtest: StandardDerivationPath[];
} {
  return {
    mainnet: Object.values(PATHS_BY_NETWORK_MAINNET),
    testnet: Object.values(PATHS_BY_NETWORK_TESTNET),
    regtest: Object.values(PATHS_BY_NETWORK_REGTEST),
  };
}
